import { ValidationError } from '@quipa/api';
import React, { PureComponent } from 'react';
import { connect, DispatchProp } from 'react-redux';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { ContentSection } from '../components/content/content_section';
import { PageContent } from '../components/content/page_content';
import { SortTable, SortTableRowInfo } from '../components/UI/table/sort_table';
import SupplierModal from '../containers/supplier/supplier_modal';
import { RootState, SuppliersModule } from '../redux';
import { SupplierContainer } from './supplier';
import { addSupplier, searchSuppliers } from '../api/supplier';
import { getAll as getAllSuppliers } from '../api/supplier';
import Header, { HeaderProps } from '../components/UI/header_with_search_select/header';
import { Supplier } from '../interfaces/supplier';
import { MixpanelEvents } from '../components/analytics/mix_panel_events';
import { trackView } from '../components/analytics/track_view';
import { FormValidationError } from '../ui_components/validation/validationForm';
import { getAccount } from '../api/account';
import { readCachedPricingPermission } from '../helper/pricingPermission';

// tslint:disable-next-line:no-empty-interface
interface SupplierOwnProps extends RouteComponentProps {
  // setFocusFirstField(fn: any): void;
}

// tslint:disable-next-line:no-empty-interface
interface SuppliersProps extends SupplierOwnProps {
  accountId: number;
}
interface SupplierModalProps {
  isOpenModal: boolean;
  handleCloseModal: () => void;
  accountId: number;
}

interface State {
  isAddSupplierModalOpen: boolean;
  suppliers: SortTableRowInfo[];
  nameSort: 'asc' | 'desc';
  includeInactive: boolean;
  scrollTop?: number;
  isLoading?: boolean;
  isUnique: boolean;
  triggerFieldFocus: false;
  accountCanSeePricing: boolean;
}

export class Suppliers extends PureComponent<
  SuppliersProps & SupplierModalProps & DispatchProp,
  State
> {
  public state: State = {
    isAddSupplierModalOpen: false,
    includeInactive: false,
    suppliers: [],
    nameSort: 'asc',
    isLoading: false,
    isUnique: true,
    triggerFieldFocus: false,
    accountCanSeePricing: readCachedPricingPermission(false),
  };

  public setFocusFirstFieldOnSupplierModal: any;

  public async componentDidMount(): Promise<void> {
    // Track View with Mixpanel
    trackView(MixpanelEvents.SUPPLIERS, {});
    // End of code for Track View with Mixpanel
    if (!this.props.isOpenModal) this.setState((state) => ({ ...state, isLoading: true }));
    if (this.props.accountId) {
      try {
        const account = await getAccount(this.props.accountId);
        this.setState((prev) => ({ ...prev, accountCanSeePricing: !!account?.canSeePricing }));
      } catch (error) {
        console.error('Error resolving pricing access:', error);
      }
    }
    await this.loadSuppliers();
    this.setState((state) => ({ ...state, isLoading: false }));
  }

  public render(): JSX.Element {
    /* tslint:disable:jsx-alignment */
    const { accountId } = this.props;
    return (
      <PageContent>
        <ContentSection>
          {window.location.pathname === '/suppliers' && (
            <Header
              headingText="Suppliers"
              addButtonText="Add New Supplier"
              inactiveFilter={this.state.includeInactive}
              onSearch={this.handleSearch}
              onInactiveFilterChange={this.toggleInactiveFilter}
              onAddClick={this.showAddSupplierModal}
              onItemClick={this.selectSupplier}
              showAddButton={this.state.accountCanSeePricing}
            />
          )}
          <Switch>
            <Route
              path="/suppliers/:id"
              render={(props) => {
                const queryParams = new URLSearchParams(props.location.search);
                const modalOpen = JSON.parse(queryParams.get('openModal') || 'false');
                return (
                  <SupplierContainer
                    supplierId={parseInt(props.match.params.id, 10)}
                    onClose={this.closeSupplier}
                    onSupplierChanged={this.onSupplierChanged}
                    modalOpen={modalOpen}
                  />
                );
              }}
            />
            <Route
              path="/suppliers"
              render={() => (
                <SortTable
                  isLoading={this.state.isLoading}
                  sort={this.state.nameSort}
                  onSortingChanged={this.handleSortingChange}
                  list={this.state.suppliers}
                  onRowChoosed={this.selectSupplier}
                  scrollTop={this.state.scrollTop}
                />
              )}
            />
          </Switch>
          <SupplierModal
            accountId={accountId}
            isModalOpen={this.state.isAddSupplierModalOpen || this.props.isOpenModal}
            onModalClose={this.closeAddSupplierModal}
            resetOnClose
            onSave={this.createSupplier}
            setFocusFirstField={(fn) => (this.setFocusFirstFieldOnSupplierModal = fn)}
            isUnique={this.state.isUnique}
            triggerFieldFocus={this.state.triggerFieldFocus}
            handleChangeUniqueName={this.handleChangeUniqueName}
          />
        </ContentSection>
      </PageContent>
    );
  }

  private showAddSupplierModal = () => {
    this.setState(
      {
        isAddSupplierModalOpen: true,
        isLoading: false,
      },
      () => {
        if (this.setFocusFirstFieldOnSupplierModal) {
          this.setFocusFirstFieldOnSupplierModal();
        }
      }
    );
  };

  private handleSearch: HeaderProps['onSearch'] = async (search) => {
    const { accountId } = this.props;
    const res = await searchSuppliers({
      accountId,
      text: search,
      nameSort: 'asc',
      typeSort: 'asc',
      includeInactive: true,
    });
    if (res.error) {
      return [];
    }
    const resFinal = res.filter((sup: any) => sup.active !== false);
    if (resFinal && !this.state.includeInactive) {
      return resFinal.map((s: any) => ({
        id: s.id,
        displayName: s.displayName,
        tags: s.tags,
      }));
    }
    return res.map((s: any) => ({
      id: s.id,
      displayName: s.displayName,
      tags: s.tags,
    }));
  };

  private toggleInactiveFilter = (includeInactive: boolean) => {
    this.setState(
      {
        includeInactive,
      },
      () => {
        this.loadSuppliers();
      }
    );
  };

  private closeSupplier = () => {
    const { history } = this.props;
    history.push('/suppliers');
  };

  private selectSupplier = (id: number, scrollTop?: number) => {
    this.setState({ scrollTop });
    const { history } = this.props;
    history.push(`/suppliers/${id}`);
  };

  private closeAddSupplierModal = () => {
    this.setState({
      isAddSupplierModalOpen: false,
      isUnique: true,
    });
    this.props.handleCloseModal();
  };

  public handleChangeUniqueName = () => {
    this.setState((prev) => ({ ...prev, isUnique: true }));
  };

  private onSupplierChanged = (supplier: SuppliersModule.BasicSupplier) => {
    this.loadSuppliers();
  };

  private handleSortingChange = (sort: 'asc' | 'desc') => {
    this.setState(
      {
        nameSort: sort,
      },
      () => this.loadSuppliers()
    );
  };

  private createSupplier = async (supplier: SuppliersModule.Supplier) => {
    const { accountId } = this.props;
    const res = await addSupplier({
      ...supplier,
      isCustom: false,
      accountId,
    });
    if (res.error) {
      if (res.name === 'ValidationError') {
        const errors = (res as Error as ValidationError).errors;
        if (errors.displayName) {
          errors.displayName = errors.displayName.replace('DisplayName', 'Display Name');
        }
        throw FormValidationError.fromErrorDictionary(errors);
      } else if (res.msg || res.message) {
        this.setState((prev: any) => ({
          ...prev,
          isUnique: false,
          triggerFieldFocus: !prev.triggerFieldFocus,
        }));
        throw new Error(res.msg || res.message);
      } else {
        throw new Error(res.message);
      }
    }
    this.setState((prev) => ({ ...prev, isUnique: true }));
    this.loadSuppliers();
    this.props.handleCloseModal();
  };

  private async loadSuppliers(): Promise<void> {
    const { accountId } = this.props;
    const { nameSort, includeInactive } = this.state;
    const res: Supplier[] = await getAllSuppliers({
      accountId,
      nameSort,
      typeSort: 'asc',
      includeInactive,
    });

    const convertToSortTableRowInfo = (supplier: Supplier) => {
      const rowInfo: SortTableRowInfo = {
        id: supplier.id,
        active: supplier.active,
        avatarId: supplier.avatarId,
        displayName: supplier.displayName,
        companyName: supplier.companyName,
        email: supplier.shippingAddress.email,
        cellPhone: supplier.shippingAddress.cellPhone,
        homePhone: supplier.shippingAddress.homePhone,
        officePhone: supplier.shippingAddress.officePhone,
      };
      return rowInfo;
    };

    this.setState({
      suppliers: res.map(convertToSortTableRowInfo),
    });
  }
}

function mapStateToProps(state: RootState, ownProps: SupplierOwnProps): SuppliersProps {
  return {
    ...ownProps,
    accountId: parseFloat(localStorage.getItem('accountId') || '0'),
    // setFocusFirstField: ownProps.setFocusFirstField,
  };
}

export const SuppliersContainer = connect(mapStateToProps)(Suppliers);
